import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import RoleGuard, { homePathForRole } from "./auth/RoleGuard";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DoctorHomePage from "./pages/DoctorHomePage";
import CreatePatientPage from "./pages/CreatePatientPage";
import FindPatientPage from "./pages/FindPatientPage";
import PatientDetailPage from "./pages/PatientDetailPage";
import MyPatientsPage from "./pages/MyPatientsPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import ScanResultsPage from "./pages/ScanResultsPage";
import PhysicianHomePage from "./pages/PhysicianHomePage";
import PhysicianPatientsPage from "./pages/PhysicianPatientsPage";
import PhysicianCreatePatientPage from "./pages/PhysicianCreatePatientPage";
import PhysicianPatientPage from "./pages/PhysicianPatientPage";
import PhysicianScanPage from "./pages/PhysicianScanPage";
import PhysicianTeamPage from "./pages/PhysicianTeamPage";
import CalculatorPage from "./pages/CalculatorPage";
import HomeGate from "./landing/HomeGate";

const TestRunnerPage = lazy(() => import("./testRunner/TestRunnerPage"));


function RoleRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homePathForRole(user.role, user.is_superuser)} replace />;
}

function AuthenticatedApp() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<RoleRedirect />} />

          <Route element={<Layout />}>
              {/* Doctor / Assistant routes (исследовательский кабинет) */}
              <Route element={<RoleGuard allowedRoles={["doctor", "assistant"]} />}>
                <Route path="/doctor" element={<DoctorHomePage />} />
                <Route path="/doctor/find-patient" element={<FindPatientPage />} />
                <Route path="/doctor/my-patients" element={<MyPatientsPage />} />
              </Route>

              {/* Admin routes */}
              <Route element={<RoleGuard allowedRoles={["admin"]} />}>
                <Route path="/admin" element={<AdminDashboardPage />} />
              </Route>

              {/* Shared: create patient (doctor, assistant, admin) */}
              <Route element={<RoleGuard allowedRoles={["doctor", "assistant", "admin"]} />}>
                <Route path="/doctor/create-patient" element={<CreatePatientPage />} />
              </Route>

              {/* Physician + его медсёстры (production-кабинет, English UI):
                  пациенты команды, создание, запуск теста / одноразовые ссылки */}
              <Route element={<RoleGuard allowedRoles={["physician", "test_administrator"]} />}>
                <Route path="/physician" element={<PhysicianHomePage />} />
                <Route path="/physician/patients" element={<PhysicianPatientsPage />} />
                <Route path="/physician/create-patient" element={<PhysicianCreatePatientPage />} />
                <Route path="/physician/patient/:patientId" element={<PhysicianPatientPage />} />
              </Route>
              {/* результаты и команда — только врач (медсёстры результатов не видят) */}
              <Route element={<RoleGuard allowedRoles={["physician"]} />}>
                <Route path="/physician/patient/:patientId/scan/:scanId" element={<PhysicianScanPage />} />
                <Route path="/physician/team" element={<PhysicianTeamPage />} />
              </Route>

              {/* Исследовательская карточка пациента (MoCA/Qmci и т.д.) —
                  physician/test_administrator сюда не попадают (redirect в свой кабинет) */}
              <Route element={<RoleGuard allowedRoles={["doctor", "assistant", "admin"]} />}>
                <Route path="/patient/:patientId" element={<PatientDetailPage />} />
              </Route>

              {/* Кабинет результатов единого теста (sacca3.0) — doctor + admin */}
              <Route element={<RoleGuard allowedRoles={["doctor", "admin"]} />}>
                <Route path="/patient/:patientId/scan/:scanId" element={<ScanResultsPage />} />
              </Route>
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public, frontend-only calculator. It is intentionally outside
            AuthProvider so opening it makes no API requests. */}
        <Route path="/calculator" element={<CalculatorPage />} />
        {/* Веб-прохождение теста по одноразовой ссылке — публично, без AuthProvider */}
        <Route
          path="/test/:token"
          element={
            <Suspense fallback={null}>
              <TestRunnerPage />
            </Suspense>
          }
        />
        <Route path="/" element={<HomeGate app={<AuthenticatedApp />} />} />
        <Route path="*" element={<AuthenticatedApp />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
